/**
 * The splash reel — the short gameplay clip Explore and the game page play in
 * a creation's trailer — and the ONE set of rules that judge it.
 *
 * The builder records it at publication (`services/builder/src/audit/reel.ts`)
 * and stamps the outcome on `VersionGates.splashReel`; the fleet probe
 * (`tools/ops/audit-live-reels.mjs`) and the reel backfill judge published
 * clips with the same {@link classifyReelMotion}. Every published version gets
 * an outcome, so "no clip" is always a named reason, never silence.
 *
 * A missing or static reel is ADVISORY (owner decision 2026-11-04): it never
 * blocks publication or the public flip. It raises {@link splashReelWarning}
 * for the creator, and the trailer shows the animated cover story instead.
 */

/** Why a version has no published clip. */
export const SPLASH_REEL_SKIP_REASONS = [
  /** The bundle never exposed the `__DJGG__` seam. */
  'no-boot',
  /** Neither the attract demo nor the menu's Play reached an active match. */
  'no-active',
  /** No canvas to record. */
  'no-canvas',
  /** The browser offered no WebM MediaRecorder. */
  'no-encoder',
  /** The recording did not finish inside its wall. */
  'record-timeout',
  /** The encoder output was empty, malformed, or over the size budget. */
  'bad-bytes',
  /** A measured (composed) build had too little window left to record. */
  'no-build-window',
  /** The recording browser itself failed. */
  'infra',
] as const;
export type SplashReelSkipReason = (typeof SPLASH_REEL_SKIP_REASONS)[number];

/** How the recorder reached gameplay. `demo` = the shell's all-bot attract
 *  mode (games with a bot); `play` = the menu's Play, driven by the platform
 *  input pilot (games without one). */
export type SplashReelEntry = 'demo' | 'play';

/** Frame-to-frame change measured on the recorded clip itself. */
export interface SplashReelMotionV1 {
  /** Sampled frames (one per {@link REEL_MOTION_SAMPLE_SECONDS} of media). */
  samples: number;
  /** Mean absolute RGB change (0–255) between consecutive samples. */
  meanStep: number;
  /** Longest run, in media seconds, with no visible change. */
  frozenSeconds: number;
}

export interface SplashReelOutcomeV1 {
  schemaVersion: 1;
  /** `recorded` = a moving clip was published; `static` = recorded but judged
   *  static, so it was NOT published; `skipped` = nothing was recorded. */
  status: 'recorded' | 'static' | 'skipped';
  entry?: SplashReelEntry;
  reason?: SplashReelSkipReason;
  /** Bounded diagnostic text for operators (never shown verbatim to players). */
  detail?: string;
  motion?: SplashReelMotionV1;
}

/** Sampling cadence of the motion measurement, in media seconds. */
export const REEL_MOTION_SAMPLE_SECONDS = 0.5;
/** Width the frames are downscaled to before differencing. */
export const REEL_MOTION_SAMPLE_WIDTH = 64;
/** A consecutive-sample change below this counts as "no visible change". */
export const REEL_MOTION_FROZEN_STEP = 0.4;
/** A clip whose mean change is below this reads as a still image. */
export const REEL_MOTION_MIN_MEAN_STEP = 0.5;
/** A clip frozen this long reads as a still image in a 12 s window. */
export const REEL_MOTION_MAX_FROZEN_SECONDS = 4;

/** Calibrated on the 45 published reels of 2026-11-04: exactly the seven
 *  clips a viewer reads as a still (idle bots, a frozen board) fail it, and
 *  every clip with visible play passes. */
export function classifyReelMotion(motion: SplashReelMotionV1): 'moving' | 'static' {
  if (motion.samples < 2) return 'static';
  return motion.meanStep < REEL_MOTION_MIN_MEAN_STEP || motion.frozenSeconds >= REEL_MOTION_MAX_FROZEN_SECONDS
    ? 'static'
    : 'moving';
}

/** Reduce consecutive-sample changes to the {@link SplashReelMotionV1} record. */
export function summarizeReelMotion(steps: readonly number[]): SplashReelMotionV1 {
  let run = 0;
  let longest = 0;
  for (const step of steps) {
    run = step < REEL_MOTION_FROZEN_STEP ? run + 1 : 0;
    longest = Math.max(longest, run);
  }
  const mean = steps.length ? steps.reduce((a, b) => a + b, 0) / steps.length : 0;
  return {
    samples: steps.length + 1,
    meanStep: Math.round(mean * 100) / 100,
    frozenSeconds: Math.round(longest * REEL_MOTION_SAMPLE_SECONDS * 10) / 10,
  };
}

const SKIP_TEXT: Record<SplashReelSkipReason, string> = {
  'no-boot': 'the game did not start in the recorder',
  'no-active': 'the recorder could not get a match running from the menu',
  'no-canvas': 'the game drew no canvas to record',
  'no-encoder': 'the recorder had no video encoder',
  'record-timeout': 'the recording did not finish in time',
  'bad-bytes': 'the recorded video was unusable',
  'no-build-window': 'the build ran out of time before recording',
  infra: 'the recorder failed',
};

/** The creator-visible warning for a version's reel, or null when its clip
 *  was published. Absent outcome = a version that predates the record. */
export function splashReelWarning(outcome: SplashReelOutcomeV1 | null | undefined): string | null {
  if (!outcome || outcome.status === 'recorded') return null;
  const tail = 'Explore shows your cover art instead of a gameplay clip.';
  if (outcome.status === 'static') return `No gameplay preview: the recorded clip barely moves. ${tail}`;
  return `No gameplay preview: ${SKIP_TEXT[outcome.reason ?? 'infra']}. ${tail}`;
}
